// Reusable modal for the «🖨 QR» buttons in /admin/stations and
// /admin/inventory.  Renders two dropdowns (agent + printer) prefilled
// with the template's default; operator can override before printing.
//
// «Не печатать — сохранить в PDF» (pdf-sink) доступно тут так же, как на
// главной странице печати — экономим бумагу, часто нужен именно PDF, а не
// физическая печать.  После enqueue модал НЕ закрывается, а показывает статус
// задания и кнопки «Открыть / Скачать PDF» (общий PrintJobResult), чтобы
// забрать файл сразу, не идя в историю печати.
//
// Сам enqueue делает caller (onPrint) — он возвращает { job_id, status }.

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PrintJobResult } from "@/components/PrintJobResult";
import { printApi, PDF_SINK_AGENT_ID, PDF_SINK_LABEL } from "@/lib/api";

export interface PrintQrTarget {
  agent_id?: string;
  printer_name?: string;
}

export interface PrintQrModalProps {
  /** What template kind drives the dropdown defaults. */
  templateKind: "station-qr" | "roll-qr" | string;
  /** Human-readable subtitle ("STATION-01", "UHR-0528"). */
  subjectLabel: string;
  /** Caller does the actual enqueue and returns the created job. */
  onPrint: (target: PrintQrTarget) => Promise<{ job_id: string; status: string }>;
  onClose: () => void;
}

export function PrintQrModal({
  templateKind,
  subjectLabel,
  onPrint,
  onClose,
}: PrintQrModalProps) {
  const { t } = useTranslation();
  const agentsQ = useQuery({
    queryKey: ["print", "agents", "online"],
    queryFn: () => printApi.listOnlineAgents(),
    staleTime: 5_000,
  });
  const templateQ = useQuery({
    queryKey: ["print", "templates", "by-kind", templateKind],
    queryFn: () => printApi.listTemplates({ kind: templateKind }),
    staleTime: 30_000,
  });

  const [agentId, setAgentId] = useState<string>("");
  const [printerName, setPrinterName] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Созданное задание — после enqueue показываем статус + кнопки скачивания.
  const [result, setResult] = useState<{ job_id: string; status: string } | null>(null);

  const isPdf = agentId === PDF_SINK_AGENT_ID;

  // Apply template defaults once both queries finish.  We don't clobber
  // an operator's manual pick if they've already touched the dropdowns;
  // useEffect deps watch the data only.
  useEffect(() => {
    if (agentId !== "" || printerName !== "") return;
    const tpl = templateQ.data?.items?.[0];
    if (!tpl) return;
    if (tpl.default_agent_id) setAgentId(tpl.default_agent_id);
    if (tpl.default_printer_name) setPrinterName(tpl.default_printer_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateQ.data]);

  const agents = agentsQ.data?.items ?? [];
  const selectedAgent = agents.find((a) => a.id === agentId);
  const printers = selectedAgent?.printers ?? [];

  // If the operator changes agent, drop a now-invalid printer choice.
  useEffect(() => {
    if (!printerName) return;
    if (printers.some((p) => p.name === printerName)) return;
    setPrinterName("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  async function fire() {
    setBusy(true);
    setErr(null);
    try {
      const r = await onPrint({
        ...(agentId ? { agent_id: agentId } : {}),
        // pdf-sink не печатает — принтер не нужен.
        ...(!isPdf && printerName ? { printer_name: printerName } : {}),
      });
      setResult(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const loading = agentsQ.isLoading || templateQ.isLoading;

  return (
    <div className="fixed inset-0 bg-page/70 backdrop-blur z-30 flex items-center justify-center p-6">
      <div className="card p-6 w-full max-w-md space-y-4">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg">{t("printQr.title")}</h2>
            <p className="text-xs text-text-muted font-mono">{subjectLabel}</p>
          </div>
          <button type="button" className="btn text-xs" onClick={onClose} disabled={busy}>
            ×
          </button>
        </header>

        {result ? (
          // ── Задание создано: статус + «Открыть / Скачать PDF» ──
          <>
            <PrintJobResult
              jobId={result.job_id}
              initialStatus={result.status}
              autoDownload={isPdf}
            />
            <div className="flex items-center justify-end pt-2 border-t border-border">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t("common.close")}
              </button>
            </div>
          </>
        ) : loading ? (
          <p className="text-text-muted text-sm">{t("printQr.loadingPrinters")}</p>
        ) : (
          <>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
                {t("printQr.agentLabel")}
              </span>
              <select
                className="w-full px-3 py-2 rounded-md bg-surface border border-border mt-1"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                disabled={busy}
              >
                <option value="">{t("printQr.autoAny")}</option>
                <option value={PDF_SINK_AGENT_ID}>📄 {PDF_SINK_LABEL}</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>

            {isPdf ? (
              <p className="text-xs text-text-muted">
                PDF не отправляется на принтер — после готовности его можно открыть и
                скачать прямо здесь.
              </p>
            ) : (
              <label className="block">
                <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
                  {t("printQr.printerLabel")}
                </span>
                <select
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border mt-1 disabled:opacity-50"
                  value={printerName}
                  onChange={(e) => setPrinterName(e.target.value)}
                  disabled={busy || !agentId}
                >
                  <option value="">
                    {agentId ? t("printQr.choose") : t("printQr.chooseAgentFirst")}
                  </option>
                  {printers.map((p) => (
                    <option key={p.id} value={p.name}>
                      {p.name}
                      {p.windows_name ? ` (${p.windows_name})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {!isPdf && <p className="text-xs text-text-muted">{t("printQr.defaultHint")}</p>}

            {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <button type="button" className="btn" onClick={onClose} disabled={busy}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={fire}
                disabled={busy || loading}
              >
                {busy
                  ? t("printQr.enqueuing")
                  : isPdf
                    ? `📄 ${PDF_SINK_LABEL}`
                    : t("printQr.print")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
